import { Card, Table, Tag, theme } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import type { PricingPayload, ModelPricing } from '../types';
import { ProviderIcon } from '@/components/ProviderIcon';

interface Props {
  pricing: PricingPayload;
}

/**
 * Read-only catalog of every model the platform serves, with its exact
 * per-million-token prices. Sourced live from `config/ai_models.json` —
 * the simulator uses these same numbers, so the user can verify accuracy.
 */
export function ModelPriceReference({ pricing }: Props) {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const rows = pricing.models.map((m, idx) => ({ key: `${m.id}-${idx}`, ...m }));

  // Percentage widths — model gets 40%, the three price columns split the rest.
  // tableLayout=fixed honors these strictly; no column ever overflows or
  // ellipses-truncates dollar values.
  const numCol = (
    title: string,
    dataIndex: string,
    opts: { dim?: boolean } = {},
  ) => ({
    title: (
      <span style={{ whiteSpace: 'nowrap' }}>
        {title}
        <span style={{ fontWeight: 400, fontSize: 10, opacity: 0.55, marginInlineStart: 4 }}>/1M</span>
      </span>
    ),
    dataIndex,
    key: dataIndex,
    align: 'right' as const,
    width: '20%',
    render: (v: number) => (
      <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, opacity: opts.dim ? 0.75 : 1, whiteSpace: 'nowrap' }}>
        ${v}
      </span>
    ),
  });

  const columns = [
    {
      title: t('chat.model'),
      dataIndex: 'name',
      key: 'name',
      width: '40%',
      render: (n: string, r: ModelPricing) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ color: token.colorTextSecondary, display: 'inline-flex', flexShrink: 0 }}>
            <ProviderIcon provider={r.provider} size={14} />
          </span>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontWeight: 500, fontSize: 13, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n}</div>
            <div style={{ fontSize: 10, opacity: 0.5, fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.id.replace(/-preview$/, '')}</div>
          </div>
        </div>
      ),
    },
    numCol('In', 'input_per_million'),
    numCol('Out', 'output_per_million'),
    numCol('Cached', 'cached_input_per_million', { dim: true }),
  ];

  // Group models visually by provider via a top header row using providers' label
  const providers = Array.from(new Set(pricing.models.map(m => m.provider_name)));

  return (
    <Card size="small" title={`Model price reference (${pricing.models.length} models)`}>
      <div style={{ fontSize: 11, opacity: 0.55, marginBlockEnd: 8 }}>
        Live from <code>config/ai_models.json</code> · models v{pricing.models_version}. Prices per 1M tokens.
        <span style={{ marginInlineStart: 8 }}>
          {providers.map(p => (
            <Tag key={p} style={{ fontSize: 9, marginInlineEnd: 3 }}>{p}</Tag>
          ))}
        </span>
      </div>
      <Table
        rowKey="key"
        dataSource={rows}
        columns={columns as any}
        size="small"
        pagination={false}
        tableLayout="fixed"
        scroll={undefined}
        style={{ background: token.colorBgContainer }}
      />
    </Card>
  );
}
